import React, { useEffect, useMemo, useState } from "react";
import { FileDown } from "lucide-react";
import TopBar from "../../components/TopBar";

function todayISO() {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

export default function Infestanti() {
    // Form
    const [dataControllo, setDataControllo] = useState(todayISO());
    const [postazione, setPostazione] = useState("");
    const [esito, setEsito] = useState("negativo");
    const [firma, setFirma] = useState("");
    const [note, setNote] = useState("");

    // Lista
    const [from, setFrom] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    });
    const [to, setTo] = useState(todayISO());
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [okMsg, setOkMsg] = useState(null);

    const warning = useMemo(() => esito === "positivo", [esito]);

    async function loadList() {
        setLoading(true);
        setError(null);
        try {
            const r = await fetch(
                `/api/infestanti/log-list.php?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
                { credentials: "include" }
            );
            const j = await r.json();
            if (!r.ok || !j.ok) throw new Error(j?.message || `HTTP ${r.status}`);
            setItems(j.items || []);
        } catch (e) {
            setError(e.message || "Errore caricamento");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadList();
        // eslint-disable-next-line
    }, []);

    async function submit() {
        setSaving(true);
        setError(null);
        setOkMsg(null);
        try {
            if (!dataControllo) throw new Error("Inserisci la data controllo.");
            if (!postazione.trim()) throw new Error("Inserisci la postazione.");
            if (!firma.trim()) throw new Error("Inserisci la firma RSA.");
            if (esito !== "positivo" && esito !== "negativo")
                throw new Error("Esito non valido.");

            const r = await fetch("/api/infestanti/log-create.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    data_controllo: dataControllo,
                    postazione: postazione.trim(),
                    esito,
                    firma_rsa: firma.trim(),
                    note: note.trim(),
                }),
            });

            const j = await r.json();
            if (!r.ok || !j.ok) throw new Error(j?.message || `HTTP ${r.status}`);

            setOkMsg("Registrazione salvata.");
            setPostazione("");
            setNote("");
            await loadList();
        } catch (e) {
            setError(e.message || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    }

    function openPdfOne(id) {
        if (!id) return;
        const url = `/api/infestanti/log-pdf-one.php?id=${encodeURIComponent(id)}`;
        window.open(url, "_blank", "noopener,noreferrer");
    }

    return (
        <>
            <TopBar />

            <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6">
                <div className="bg-white border border-gray-200 rounded-2xl p-5">
                    <h1 className="text-2xl font-bold">Monitoraggio interno infestanti</h1>
                    <p className="text-sm text-gray-600 mt-1">
                        Compila il controllo periodico delle postazioni. In caso di esito positivo,
                        avvisa tempestivamente la ditta specializzata.
                    </p>

                    {warning && (
                        <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800 text-sm">
                            ⚠️ Esito <b>POSITIVO</b>: avvisa la ditta specializzata tempestivamente e registra eventuali azioni correttive.
                        </div>
                    )}

                    <div className="mt-5 grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-800">Data controllo</label>
                            <input
                                type="date"
                                className="mt-1 w-full border rounded-lg p-2"
                                value={dataControllo}
                                onChange={(e) => setDataControllo(e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-800">Postazione *</label>
                            <input
                                className="mt-1 w-full border rounded-lg p-2"
                                placeholder="Es: Cucina - esca n.2 / Magazzino - angolo Ovest"
                                value={postazione}
                                onChange={(e) => setPostazione(e.target.value)}
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-800">Esito *</label>
                            <div className="mt-2 flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setEsito("negativo")}
                                    className={`px-3 py-2 rounded-lg border ${esito === "negativo"
                                            ? "bg-emerald-600 text-white border-emerald-600"
                                            : "bg-white"
                                        }`}
                                >
                                    Negativo
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setEsito("positivo")}
                                    className={`px-3 py-2 rounded-lg border ${esito === "positivo"
                                            ? "bg-rose-600 text-white border-rose-600"
                                            : "bg-white"
                                        }`}
                                >
                                    Positivo
                                </button>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">
                                Seleziona “Positivo” solo se noti presenza/segni di infestanti.
                            </p>
                        </div>

                        <div>
                            <label className="text-sm font-medium text-gray-800">Firma RSA *</label>
                            <input
                                className="mt-1 w-full border rounded-lg p-2"
                                placeholder="Nome e Cognome"
                                value={firma}
                                onChange={(e) => setFirma(e.target.value)}
                            />
                        </div>

                        <div className="md:col-span-2">
                            <label className="text-sm font-medium text-gray-800">Note (opzionale)</label>
                            <input
                                className="mt-1 w-full border rounded-lg p-2"
                                placeholder="Es: esca sostituita / pulizia area / segnalato a ditta…"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                            />
                        </div>
                    </div>

                    {error && <div className="mt-4 text-red-600 text-sm">{error}</div>}
                    {okMsg && <div className="mt-4 text-emerald-700 text-sm">{okMsg}</div>}

                    <div className="mt-5">
                        <button
                            type="button"
                            onClick={submit}
                            disabled={saving}
                            className={`px-4 py-2 rounded-lg text-white ${saving ? "bg-gray-400" : "bg-blue-600 hover:bg-blue-700"
                                }`}
                        >
                            {saving ? "Salvataggio…" : "Salva controllo"}
                        </button>
                    </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-2xl p-5">
                    <div className="flex flex-col md:flex-row md:items-end gap-3 md:justify-between">
                        <div>
                            <h2 className="text-lg font-semibold">Storico controlli</h2>
                            <p className="text-xs text-gray-500">Mostra fino a 500 righe nel periodo selezionato.</p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            <div>
                                <label className="text-xs text-gray-600">Da</label>
                                <input
                                    type="date"
                                    className="block border rounded-lg p-2"
                                    value={from}
                                    onChange={(e) => setFrom(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600">A</label>
                                <input
                                    type="date"
                                    className="block border rounded-lg p-2"
                                    value={to}
                                    onChange={(e) => setTo(e.target.value)}
                                />
                            </div>
                            <button
                                type="button"
                                onClick={loadList}
                                disabled={loading}
                                className="h-10 mt-5 md:mt-0 px-3 rounded-lg border hover:bg-gray-50"
                            >
                                {loading ? "Carico…" : "Aggiorna"}
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 overflow-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left border-b">
                                    <th className="py-2 pr-3">Data</th>
                                    <th className="py-2 pr-3">Postazione</th>
                                    <th className="py-2 pr-3">Esito</th>
                                    <th className="py-2 pr-3">Firma RSA</th>
                                    <th className="py-2 pr-3">Note</th>
                                    <th className="py-2 pr-3">Inserito</th>
                                    <th className="py-2 pr-3 text-right">PDF</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="py-6 text-center text-gray-500">
                                            Nessun controllo nel periodo selezionato.
                                        </td>
                                    </tr>
                                )}

                                {items.map((it) => (
                                    <tr key={it.id} className="border-b">
                                        <td className="py-2 pr-3 whitespace-nowrap">{it.data_controllo}</td>
                                        <td className="py-2 pr-3">{it.postazione}</td>
                                        <td className="py-2 pr-3">
                                            <span
                                                className={`px-2 py-1 rounded-full text-xs border ${it.esito === "positivo"
                                                        ? "bg-rose-50 text-rose-700 border-rose-200"
                                                        : "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                    }`}
                                            >
                                                {it.esito}
                                            </span>
                                        </td>
                                        <td className="py-2 pr-3">{it.firma_rsa}</td>
                                        <td className="py-2 pr-3">{it.note || ""}</td>
                                        <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">{it.created_at}</td>
                                        <td className="py-2 pr-3 text-right">
                                            <button
                                                type="button"
                                                onClick={() => openPdfOne(it.id)}
                                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border hover:bg-gray-50"
                                                title="Apri PDF"
                                            >
                                                <FileDown size={16} />
                                                <span className="hidden sm:inline">PDF</span>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="mt-3 text-xs text-gray-500">
                        Suggerimento: clicca “PDF” su una riga per stampare la registrazione singola con carta intestata.
                    </div>
                </div>
            </div>
        </>
    );
}